// src/pages/manager/Gallery.js
import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Container,
  Box,
  IconButton,
  Typography,
  Divider,
  List,
  ListItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import {
  ArrowBack,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import Title from "../../ui/Title";
import { MANAGER_ROUTES } from "../../utils/constants";
import { useBmapi } from "../../utils/bmapi-context";
import { getErrorMessageString } from "../../utils/errors";
import PostSelection from "../../components/PostSelection";
import GalleryItemDialog from "../../components/GalleryItemDialog";
import PostPreview from "../../components/PostPreview";

export default function Gallery() {
  const intl = useIntl();
  const { bmapi, notifyError /*, notifySuccess*/ } = useBmapi();

  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const section = params.get("section");

  const MIN_SPINNER_MS = 300;
  const [busy, setBusy] = useState(false);
  const [gallery, setGallery] = useState(null);

  // stato dialog (usato sia per edit che per add)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("edit"); // "edit" | "add"
  const [dialogValues, setDialogValues] = useState({
    priority: "",
    title: "",
    description: "",
    imageUrl: "",
  });
  // elemento che stiamo modificando / aggiungendo
  const [dialogItem, setDialogItem] = useState(null);

  // dialog preview post
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);

  // dialog delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  // per forzare il refresh di PostSelection
  const [postsReloadKey, setPostsReloadKey] = useState(0);

  const hasError = !code || !section;

  const withBusy = useCallback(async (fnOrPromise) => {
    const start = Date.now();
    setBusy(true);
    try {
      if (typeof fnOrPromise === "function") {
        await fnOrPromise();
      } else {
        await fnOrPromise;
      }
    } finally {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, MIN_SPINNER_MS - elapsed);
      setTimeout(() => setBusy(false), remaining);
    }
  }, []);

  // Metadati per la riga selezionata (come nella lista ManageGalleries)
  const {
    businessFlag,
    sectionType,
    contextLabel,
    optionalBusiness,
  } = useMemo(() => {
    if (!bmapi || !code || !section) {
      return {
        businessFlag: false,
        sectionType: "",
        contextLabel: "",
        optionalBusiness: null,
      };
    }

    const userInfo = bmapi.getUserInfo();
    const businessName = userInfo?.business?.name ?? "";

    const galleriesRaw = bmapi.settings?.galleries || [];
    let items = galleriesRaw;

    // Se fosse annidato tipo [[...]], prendo il primo livello
    if (Array.isArray(items[0])) {
      items = items[0];
    }

    let flag = false;
    let type = "";
    let optionalBusiness = null;

    items.forEach((g) => {
      if (!g) return;
      const gCode = g.code || "-";
      if (gCode !== code) return;

      if (g.businessFlag) {
        flag = true;
      }

      optionalBusiness = g.optionalBusiness;

      const secs = Array.isArray(g.sections) ? g.sections : [];
      secs.forEach((s) => {
        if (!s) return;
        if (s.name === section && !type) {
          type = s.type || "";
        }
      });
    });

    const ctx = flag ? businessName : "Globale";

    return {
      businessFlag: flag,
      sectionType: type,
      contextLabel: ctx,
      optionalBusiness: optionalBusiness,
    };
  }, [bmapi, code, section]);

  const businessId = businessFlag
    ? bmapi.getUserInfo()?.business?.id ?? null
    : null;

  const loadGallery = useCallback(async () => {
    if (!bmapi || !code || !section) return;

    await withBusy(async () => {
      const resp = await bmapi.getGenContentsByCodeAndSection(
        code,
        section,
        businessId
      );
      setGallery(resp || []);
    });
  }, [bmapi, code, section, withBusy, businessId]);

  useEffect(() => {
    if (hasError) return;
    loadGallery().catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [loadGallery, notifyError, intl, hasError]);

  const sortedGallery = useMemo(() => {
    const list = gallery || [];
    // ordino per priority (crescente: 1,2,3,...)
    return [...list].sort((a, b) => (a.priority || 0) - (b.priority || 0));
  }, [gallery]);

  // click su elemento già presente in gallery (edit)
  const handleGalleryItemEdit = (item) => {
    const data = item?.data || {};
    setDialogMode("edit");
    setDialogItem(item);
    setDialogValues({
      priority:
        item?.priority !== undefined && item?.priority !== null
          ? item.priority
          : "",
      title: data.title || "",
      description: data.description || "",
      imageUrl: data.imageUrl || "",
    });
    setDialogOpen(true);
  };

  // click da PostSelection (add)
  const handleSelectPost = (selection) => {
    setDialogMode("add");
    setDialogItem(selection);
    setDialogValues({
      priority: "",
      title: selection.title || "",
      description: selection.description || "",
      imageUrl: selection.imageUrl || "",
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogConfirm = async (values) => {
    await withBusy(async () => {
      try {
        if (dialogMode === "add") {
          // creazione nuovo contenuto di gallery
          const data = {
            code: code,
            section: section,
            // se la sezione è "posts" imposta ref_type "post"
            ref_type: sectionType === "posts" ? "post" : sectionType,
            // id del post selezionato
            ref_id: dialogItem?.refId || dialogItem?.post?.id || null,
            business_id: businessId,
            data: {
              title: values.title,
              description: values.description,
              imageUrl: values.imageUrl,
            },
            priority: values.priority,
          };
          await bmapi.createGenContent(data);
        } else if (dialogMode === "edit") {
          if (!dialogItem?.id) {
            // fallback di sicurezza
            console.warn("dialogItem senza id, impossibile aggiornare");
          } else {
            // update dell'elemento esistente
            const updatePayload = {
              code: code,
              section: section,
              // se la sezione è "posts" imposta ref_type "post"
              ref_type: sectionType === "posts" ? "post" : sectionType,
              // id del post selezionato
              ref_id: dialogItem?.refId || dialogItem?.post?.id || null,
              business_id: businessId,
              data: {
                title: values.title,
                description: values.description,
                imageUrl: values.imageUrl,
              },
              priority: values.priority,
            };
            await bmapi.updateGenContent(dialogItem.id, updatePayload);
          }
        }

        // dopo il salvataggio ricarico gallery e lista post
        await loadGallery();
        setPostsReloadKey((k) => k + 1);
      } catch (e) {
        console.error(e);
        notifyError(getErrorMessageString(e, intl));
      } finally {
        setDialogOpen(false);
      }
    });
  };

  // VIEW: anteprima articolo
  const handleViewClick = async (item) => {
    await withBusy(async () => {
      try {
        const postId = item?.ref_id;
        if (!postId) return;
        const post = await bmapi.getPost(postId);
        setPreviewPost(post || null);
        setPreviewOpen(true);
      } catch (e) {
        console.error(e);
        notifyError(getErrorMessageString(e, intl));
      }
    });
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewPost(null);
  };

  // DELETE: conferma + chiamata API
  const handleDeleteClick = (item) => {
    setDeleteItem(item);
    setDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setDeleteItem(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItem) return;

    await withBusy(async () => {
      try {
        await bmapi.deleteGenContent(deleteItem.id);
        await loadGallery();
      } catch (e) {
        console.error(e);
        notifyError(getErrorMessageString(e, intl));
      } finally {
        setDeleteOpen(false);
        setDeleteItem(null);
      }
    });
  };

  const deleteTitle = deleteItem?.data?.title || "";

  return (
    <>
      <Container maxWidth="md">
        {/* Back + titolo */}
        <Box display="flex" alignItems="center" mb={1}>
          <IconButton
            component={RouterLink}
            to={MANAGER_ROUTES.GALLERIES}
            aria-label="back"
            disabled={busy}
          >
            <ArrowBack />
          </IconButton>
          <Title>
            <FormattedMessage
              id="navigation.manager.editGallery"
              defaultMessage="Modifica galleria"
            />
          </Title>
        </Box>

        {hasError ? (
          <Typography color="error" variant="body1">
            Galleria non specificata
          </Typography>
        ) : (
          <Box mt={2}>
            {/* Riga con i metadati, tutta su una riga */}
            <Typography variant="body2">
              <strong>Codice:</strong> {code}
              {"  "}
              <strong>Contesto:</strong> {contextLabel || "—"}
              {"  "}
              <strong>Nome sezione:</strong> {section}
              {"  "}
              <strong>Tipo sezione:</strong> {sectionType || "—"}
            </Typography>

            {/* Split verticale: sinistra gallery, divider, destra PostSelection (se posts) */}
            <Box mt={2} display="flex" alignItems="stretch">
              {/* Sinistra: elementi della gallery */}
              <Box flex={1} mr={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Elementi della galleria
                </Typography>

                <List>
                  {sortedGallery.map((item, idx) => {
                    const data = item?.data || {};
                    const title = data.title || "";
                    const description = data.description || "";
                    const imageUrl = data.imageUrl || "";
                    const priority =
                      item?.priority !== undefined && item?.priority !== null
                        ? item.priority
                        : "";

                    return (
                      <React.Fragment key={item.id || idx}>
                        {idx > 0 && <Divider component="li" />}
                        <ListItem>
                          <Box
                            display="flex"
                            alignItems="flex-start"
                            width="100%"
                          >
                            {/* Priority evidenziata a sinistra */}
                            <Box
                              mr={2}
                              px={1}
                              py={0.5}
                              minWidth={32}
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              borderRadius={4}
                              style={{
                                backgroundColor: "#f0f0f0",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                {priority}
                              </Typography>
                            </Box>

                            {imageUrl && (
                              <Box mr={2}>
                                <img
                                  src={imageUrl}
                                  alt={title}
                                  style={{
                                    width: 64,
                                    height: 64,
                                    objectFit: "cover",
                                    borderRadius: 4,
                                  }}
                                />
                              </Box>
                            )}

                            <Box flex={1}>
                              <Typography variant="body1">{title}</Typography>
                              {description && (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {description}
                                </Typography>
                              )}
                            </Box>

                            {/* Azioni a destra, in verticale */}
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              ml={1}
                              style={{ rowGap: 4 }}
                            >
                              <IconButton
                                aria-label="view"
                                onClick={() => handleViewClick(item)}
                                size="small"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                aria-label="edit"
                                onClick={() => handleGalleryItemEdit(item)}
                                size="small"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                aria-label="delete"
                                onClick={() => handleDeleteClick(item)}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </ListItem>
                      </React.Fragment>
                    );
                  })}

                  {(!sortedGallery || sortedGallery.length === 0) && (
                    <Typography variant="body2" color="textSecondary">
                      Nessun elemento in galleria.
                    </Typography>
                  )}
                </List>
              </Box>

              {sectionType === "posts" && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Box flex={1} ml={2}>
                    <PostSelection
                      onSelectPost={handleSelectPost}
                      reloadKey={postsReloadKey}
                      optionalBusiness={optionalBusiness}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}

        <GalleryItemDialog
          open={dialogOpen}
          mode={dialogMode}
          initialValues={dialogValues}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
        />
      </Container>

      {/* Dialog preview articolo */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Anteprima articolo</DialogTitle>
        <DialogContent dividers>
          <PostPreview post={previewPost} noFrame />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePreviewClose} color="primary">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog conferma delete */}
      <Dialog
        open={deleteOpen}
        onClose={busy ? undefined : handleDeleteCancel}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Confermi di voler cancellare l&apos;elemento della gallery{" "}
            <strong>{deleteTitle}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={busy}>
            Annulla
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="secondary"
            disabled={busy}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* OVERLAY SPINNER A SCHERMO INTERO */}
      {busy && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255, 255, 255, 0.6)"
          style={{
            zIndex: 2000, // > zIndex.modal di MUI (1300)
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
}
